import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import MaterialReactTable from "material-react-table";
import { Box, Stack } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";

import { getAuditLogsData } from "api";
import {
  COLORS,
  Loading,
  MarginWrapper,
  useTableExpandedRows,
  useYearMonthPeriodFilter,
  useYearPeriodFilter,
  EsgDatePickerField,
  BasicButton,
} from "components";

import { useNotify } from "hooks";
import { ParsedQueryParams } from "types";
import { useNewBreadcrumb } from "recoils";
import { getDateFormatset, isMoreThanOneMonthDifference } from "utils";

import { AUDIT_LOGS_PAGE, AUDIT_LOGS_COLUMNS_MAIN } from "./consts";
import { AuditLogsDetailsModal } from "./components";

const initialState = {
  isYearly: false,
  startDate: dayjs(new Date()).subtract(30, 'day').format("YYYY-MM-DD"),
  endDate: dayjs(new Date()).format("YYYY-MM-DD"),
  fromDate: null,
  toDate: null,
  view: "monthly",
};

function reducer(state: any, action: any) {
  let isYearly;
  let period;

  switch (action.type) {
    case "SET_YEARLY":
      ({ isYearly, period } = action.payload);
      return {
        ...state,
        isYearly,
        view: isYearly ? "yearly" : "monthly",
        startDate: isYearly
          ? period?.startOf("year").format("YYYY-MM-DD")
          : `${period.format("YYYY-MM")}-01`,
        endDate: isYearly
          ? period?.endOf("year").format("YYYY-MM-DD")
          : `${period.format("YYYY-MM")}-${period?.daysInMonth()}`,
      };
    case "SET_FROM_DATE":
      return {
        ...state,
        fromDate: action.payload.value,
        view: "daterange",
        startDate: action.payload.value
      };
    case "SET_TO_DATE":
      return {
        ...state,
        toDate: action.payload.value,
        view: "daterange",
        endDate: action.payload.value
      };
    case "SET_START_AND_END_DATES":
      return {
        ...state,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      };

    case "CLEAR_DATES":
      return {
        ...state,
        fromDate: null,
        toDate: null,
        startDate: dayjs(new Date()).format("YYYY-MM-DD"),
        endDate: dayjs(new Date()).format("YYYY-MM-DD"),
        isYearly: false,
        view: "monthly",
      };
    default:
      return state;
  }
};

export const AuditLogsPage = () => {
  // ---- States ----
  const [state, dispatch] = useReducer(reducer, initialState);
  const [clearDate, setClearDate] = useState(false);
  const { isYearly, startDate, endDate, fromDate } = state;
  const [dateError, setDateError] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isChanged, setChanged] = useState(false)
  const [selectedRowData, setSelectedRowData] = useState<any>(undefined);
  // ---- Hook ----
  const notify = useNotify();

  useNewBreadcrumb(AUDIT_LOGS_PAGE.TITLE);
  const { setInitialExpanded, updateQueryParamsWithInitialExpanded } =
    useTableExpandedRows();

  const updateExternalStates = useCallback(() => {
    setInitialExpanded();
  }, [setInitialExpanded]);

  const getQueryParamsWithExternalChanges = useCallback(
    (queryParams: ParsedQueryParams) =>
      updateQueryParamsWithInitialExpanded(queryParams),
    [updateQueryParamsWithInitialExpanded],
  );
  const { period, availableYears } = useYearMonthPeriodFilter({
    updateExternalStates,
    getQueryParamsWithExternalChanges,
    isYearly,
  });

  const { year } = useYearPeriodFilter({
    updateExternalStates: undefined,
    withQueryParams: undefined,
    useYears: undefined,
  });

  const minDate = dayjs(
    `${Math.min(...(availableYears || [dayjs().utc().year()]))}-01-01`,
  ).utc();
  const maxDate = dayjs().utc();

  const {
    data: auditLogs,
    isLoading,
    mutate: auditLogsMutation,
  } = useMutation(getAuditLogsData);

  const tableData = auditLogs || [];

  const handleFromDateChange = (date: Dayjs | null) => {
    if (!date) return;
    const fromDateValue = date.format("YYYY-MM-DD");
    dispatch({ type: "SET_FROM_DATE", payload: { value: fromDateValue } });
    setChanged(true)
  };

  const handleToDateChange = (date: Dayjs | null) => {
    if (!date) return;
    const toDateValue = date.format("YYYY-MM-DD");
    dispatch({ type: "SET_TO_DATE", payload: { value: toDateValue } });
    if (fromDate && toDateValue < fromDate) {
      setDateError(true);
    } else {
      dispatch({
        type: "SET_TO_DATE",
        payload: { value: toDateValue, period },
      });
      setChanged(true)
      setDateError(false);
    }
  };

  const handleClearDates = () => {
    dispatch({ type: "CLEAR_DATES", payload: null });
    setDateError(false);
    setClearDate(true);
    setTimeout(() => {
      setClearDate(false);
    }, 100);
    window.location.reload();
  };

  const handleSearch = () => {
    setChanged(false);

    if (isMoreThanOneMonthDifference(dayjs(startDate), dayjs(endDate))) {
      notify.error("We're not able to search the audit log beyond a 30 day window. Please adjust your Start and End Dates and try again.");
    } else {
      auditLogsMutation({
        fromDate: startDate,
        toDate: endDate,
      });
    }
  };

  const handleDetailsModalClose = () => {
    setSelectedRowData(undefined);
  };

  const datePickerData = [
    {
      label: "Start Date",
      onAcceptFn: handleFromDateChange,
      value: dayjs(startDate),
    },
    {
      label: "End Date",
      onAcceptFn: handleToDateChange,
      value: dayjs(endDate),
    }
  ];

  useEffect(() => {
    dispatch({
      type: "SET_FROM_DATE",
      payload: { value: period.format("YYYY-MM-DD") },
    });
    dispatch({
      type: "SET_TO_DATE",
      payload: { value: period.format("YYYY-MM-DD") },
    });
    dispatch({
      type: "SET_START_AND_END_DATES",
      payload: {
        startDate: period.format("YYYY-MM-DD"),
        endDate: period.format("YYYY-MM-DD"),
      },
    });
    // eslint-disable-next-line
  }, [period, year]);

  useEffect(() => {
    handleSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <MarginWrapper>
      <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
        {!isYearly && (
          <Stack
            direction="row"
            spacing={2}
          >
            {datePickerData?.map((obj) =>
              <EsgDatePickerField
                width="160px"
                label={obj.label}
                openTo="day"
                minDate={minDate}
                maxDate={maxDate}
                views={["year", "month", "day"]}
                format={getDateFormatset()}
                disableFuture
                onChange={() => { }}
                onAccept={obj.onAcceptFn}
                value={obj.value}
                clearDate={clearDate}
                emission
              />
            )}
            {dateError && (
              <Box sx={{ color: COLORS.Fuzzy_Brown, fontSize: "0.75rem" }}>
                Invalid date selection. Please choose a date after the From
                Date.
              </Box>
            )}
          </Stack>
        )}
        <BasicButton variant={isChanged ? "contained" : "outlined"} sx={{ width: "120px", color: isChanged ? "white" : COLORS.Romance, marginRight: "0px" }} onClick={handleSearch}>Search</BasicButton>
        <BasicButton variant="outlined" sx={{ width: "120px", color: COLORS.Romance }} onClick={handleClearDates}>Clear Dates</BasicButton>
      </Box>
      {isLoading && <Loading />}
      <div>
        {isLoading ? (
          <Loading />
        ) : (
          <MaterialReactTable
            columns={AUDIT_LOGS_COLUMNS_MAIN}
            data={tableData}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enablePagination={false}
            enableRowVirtualization
            onGlobalFilterChange={setGlobalFilter}
            state={{
              globalFilter,
            }}
            initialState={{ density: 'compact' }}
            muiTableContainerProps={{
              sx: {
                "& thead tr:first-of-type th:first-of-type": {
                  paddingLeft: "20px",
                },
                "& tbody tr td:first-of-type": {
                  paddingLeft: "20px",
                },
                "& tbody tr:first-of-type td > p": {
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                },
                height: "calc(100vh - 300px)",
                border: "1px solid #D9D9D9",
                borderRadius: "10px",
                minWidth: "100%",
                width: "100%",
                maxWidth: "100%",
                "@media (min-width: 600px)": {
                  width: "100%",
                },
                "@media (min-width: 960px)": {
                  width: "1100px",
                },
                "@media (min-width: 1280px)": {
                  width: "1100px",
                },
                "@media (min-width: 1440px)": {
                  width: "1100px",
                },
                "@media (min-width: 1680px)": {
                  width: "1300px",
                },
                "@media (min-width: 2400px)": {
                  width: "100%",
                },
              },
            }}
            muiTablePaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "none",
              },
            }}
            muiTopToolbarProps={{
              sx: {
                backgroundColor: 'white',
                '& .MuiIconButton-root': {
                  // Hide filter, sorting, and density icons
                  '&:not(:nth-of-type(1))': {
                    display: 'none',
                  },
                },
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                lineHeight: "30px",
                "& .MuiBadge-root": {
                  display: "none",
                },
              },
            }}
            muiTableHeadRowProps={{
              sx: {
                "& > th": {
                  paddingTop: 1,
                  paddingBottom: 1,
                },
                height: "auto",
                backgroundColor: "#F8FAFC",
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => {
                setSelectedRowData(row.original);
              },
              sx: {
                cursor: 'pointer',
                maxHeight: "76px",
                backgroundColor: "white",
              },
            })}
          />

        )}
        {!!selectedRowData && <AuditLogsDetailsModal isOpen={!!selectedRowData} data={selectedRowData} handleClose={handleDetailsModalClose} />}
      </div>
    </MarginWrapper>
  );
};

export default AuditLogsPage;
